import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { map, startWith } from 'rxjs';
import { MEDIUM_BREAKPOINT_QUERY } from '../../constants';
import { DropdownItem, User } from '../../models';
import { ImgFolderPipe } from '../../pipes';
import { AuthService } from '../../services';
import { LayoutMainComponent } from '../layouts/layout-main/layout-main.component';

@Component({
  selector: 'app-header-navbar',
  imports: [
    NzButtonComponent,
    ImgFolderPipe,
    LayoutMainComponent,
    NzDropDownModule,
    NzIconDirective,
    RouterLink,
    NzTooltipDirective,
  ],
  templateUrl: './header-navbar.component.html',
  styleUrl: './header-navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderNavbarComponent {
  readonly actionLabel = input<string | null>(null);
  readonly actionLink = input<string | readonly unknown[] | null>(null);
  readonly actionHidden = input(false);

  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly isMediumAndAbove = toSignal(
    this.observeMediumBreakpoint(),
    {
      initialValue: false,
    },
  );

  protected readonly accountMenuItems = signal<DropdownItem[]>([
    {
      id: 'profile',
      label: 'Go to account',
      icon: 'user',
      title: 'Go to profile section',
      action: () => this.router.navigate(['/profile']),
      show: () => true,
    },
    {
      id: 'logout',
      label: 'Log out',
      icon: 'logout',
      action: () => this.authService.logout(),
      title: 'Log out',
      show: () => true,
    },
  ]);

  protected readonly user = toSignal<User | null>(
    this.authService
      .userChanges()
      .pipe(startWith(this.authService.currentUser)),
  );

  protected readonly displayName = computed(() => {
    const currentUser = this.user();
    const firstname = currentUser?.firstname?.trim() || '';
    const lastname = currentUser?.lastname?.trim() || '';
    const parts = [firstname, lastname].filter(
      (part): part is string => part.length > 0,
    );

    if (parts.length > 0) {
      return parts.join(' ');
    }

    return 'Account';
  });

  protected readonly showAction = computed(
    () => !!this.actionLabel() && !!this.actionLink() && !this.actionHidden(),
  );

  private observeMediumBreakpoint() {
    return this.breakpointObserver
      .observe([MEDIUM_BREAKPOINT_QUERY])
      .pipe(map((state: BreakpointState) => state.matches));
  }
}
